import {
  collapseBoardLeft,
  getAnyLegalMoveIndexes,
  getLegalMoveIndexesForHand,
  hasAnyLegalMove,
  nextBestRound,
  resolveMove
} from "./helpers";
import {
  createInitialFlowState,
  extendFlowAcrossRound,
  registerFlowMove,
  resetActiveFlow
} from "./flow";
import { createInitialIncrementState, syncIncrementState } from "./increment";
import { createBoardTiles, createHandTiles, type BoardCreationResult } from "./initialState";
import {
  createInitialRefillState,
  hasRefillSpace,
  REFILL_WINDOW_MS,
  syncRefillState
} from "./refill";
import type { GameState, InitialDeal, TileGenerationContext, TileGenerator } from "./types";
import type { HighBuildValue, RecentHighBuildHint } from "./types";

const HIGH_BUILD_VALUES: ReadonlyArray<HighBuildValue> = [7, 8, 9];
const HIGH_BUILD_HINT_TTL = 3;
const MAX_HIGH_BUILD_HINTS = 3;

function toGeneratorState(
  state: Pick<
    GameState,
    | "board"
    | "hand"
    | "roundsCleared"
    | "score"
    | "turnIndex"
    | "recentHighBuildHints"
    | "gameOver"
  >
): TileGenerationContext["state"] {
  return {
    board: state.board,
    hand: state.hand,
    roundsCleared: state.roundsCleared,
    score: state.score,
    turnIndex: state.turnIndex,
    recentHighBuildHints: state.recentHighBuildHints,
    gameOver: state.gameOver
  };
}

function finalizeRunState(state: GameState): GameState {
  if (hasAnyLegalMove(state)) {
    if (!state.gameOver && state.lastRunSummary === null) {
      return state;
    }

    return {
      ...state,
      gameOver: false,
      lastRunSummary: null
    };
  }

  const scoreIsPersonalBest = state.score > state.bestScore;
  const roundIsPersonalBest = state.roundNumber > state.bestRound;
  const maxFlowIsPersonalBest = state.flow.maxCount > state.bestMaxFlow;

  return {
    ...state,
    selectedHandIndex: null,
    flow: resetActiveFlow(state.flow),
    refill: {
      ...state.refill,
      expiresAt: null
    },
    increment: {
      lanes: state.increment.lanes.map((lane) => ({ ...lane, expiresAt: null }))
    },
    gameOver: true,
    bestRound: nextBestRound(state),
    bestScore: scoreIsPersonalBest ? state.score : state.bestScore,
    bestMaxFlow: maxFlowIsPersonalBest ? state.flow.maxCount : state.bestMaxFlow,
    lastRunSummary: {
      score: {
        value: state.score,
        isPersonalBest: scoreIsPersonalBest
      },
      round: {
        value: state.roundNumber,
        isPersonalBest: roundIsPersonalBest
      },
      maxFlow: {
        value: state.flow.maxCount,
        isPersonalBest: maxFlowIsPersonalBest
      }
    }
  };
}

function drawReplacementHandTile(
  state: GameState,
  handIndex: number,
  generator: TileGenerator
): GameState {
  const remainingHand = state.hand.filter((_, index) => index !== handIndex);
  if (remainingHand.length !== 2) {
    return state;
  }

  const generatedHandTile = generator.nextTile({
    reason: "hand",
    roundNumber: state.roundNumber,
    state: toGeneratorState(state)
  });

  return {
    ...state,
    hand: [...remainingHand, generatedHandTile] as GameState["hand"]
  };
}

function decayHighBuildHints(hints: RecentHighBuildHint[]): RecentHighBuildHint[] {
  return hints
    .map((hint) => ({ ...hint, ttl: hint.ttl - 1 }))
    .filter((hint) => hint.ttl > 0);
}

function updateRecentHighBuildHints(
  state: GameState,
  resolution: ReturnType<typeof resolveMove>
): RecentHighBuildHint[] {
  const decayedHints = decayHighBuildHints(state.recentHighBuildHints);
  if (resolution.outcome !== "build" || resolution.builtValue === null) {
    return decayedHints;
  }

  const builtValue = resolution.builtValue;
  if (!HIGH_BUILD_VALUES.includes(builtValue as HighBuildValue)) {
    return decayedHints;
  }

  return [
    { value: builtValue as HighBuildValue, ttl: HIGH_BUILD_HINT_TTL },
    ...decayedHints
  ].slice(0, MAX_HIGH_BUILD_HINTS);
}

export function createInitialState(
  generator: TileGenerator,
  bestRound = 0,
  bestScore = 0,
  bestMaxFlow = 0,
  initialDeal: InitialDeal | null = null
): GameState {
  const roundNumber = 1;
  const initialBoard =
    initialDeal === null
      ? createBoardTiles(generator, roundNumber, null, 0)
      : createBoardTilesFromDeal(initialDeal, 0);
  const board = initialBoard.board;
  const hand = initialDeal === null ? createHandTiles(generator, roundNumber, null) : initialDeal.hand;

  return finalizeRunState({
    board,
    hand,
    selectedHandIndex: null,
    roundsCleared: 0,
    bestRound,
    score: 0,
    bestScore,
    bestMaxFlow,
    nextBoardTileId: initialBoard.nextBoardTileId,
    roundNumber,
    turnIndex: 0,
    recentHighBuildHints: [],
    flow: createInitialFlowState(),
    refill: createInitialRefillState({ roundNumber, score: 0 }),
    increment: createInitialIncrementState(
      { roundNumber, score: 0 },
      board,
      0,
      generator.nextRandom
    ),
    gameOver: false,
    lastRunSummary: null
  });
}

function createBoardTilesFromDeal(
  initialDeal: InitialDeal,
  nextBoardTileId: number
): BoardCreationResult {
  const board = initialDeal.board.map((value, index) => ({
    id: `b-${nextBoardTileId + index}`,
    value,
    source: "dealt" as const,
    createdTurn: 0,
    buildDepth: 0
  }));

  return {
    board,
    nextBoardTileId: nextBoardTileId + board.length
  };
}

export function startNextRound(
  state: GameState,
  generator: TileGenerator,
  startedAt: number
): GameState {
  const nextRoundNumber = state.roundNumber + 1;
  const nextBoard = createBoardTiles(
    generator,
    nextRoundNumber,
    toGeneratorState(state),
    state.nextBoardTileId
  );
  const nextState: GameState = {
    ...state,
    board: nextBoard.board,
    hand: createHandTiles(generator, nextRoundNumber, toGeneratorState(state)),
    selectedHandIndex: null,
    roundsCleared: state.roundsCleared + 1,
    nextBoardTileId: nextBoard.nextBoardTileId,
    roundNumber: nextRoundNumber,
    recentHighBuildHints: [],
    flow: extendFlowAcrossRound(state.flow),
    refill: createInitialRefillState({
      roundNumber: nextRoundNumber,
      score: state.score
    }),
    increment: createInitialIncrementState(
      {
        roundNumber: nextRoundNumber,
        score: state.score
      },
      nextBoard.board,
      startedAt,
      generator.nextRandom
    ),
    gameOver: false
  };

  return finalizeRunState(nextState);
}

export function isRoundCleared(state: GameState): boolean {
  return state.board.length === 0;
}

export function isGameOver(state: GameState): boolean {
  return state.gameOver;
}

export function getLegalMoveIndexes(state: GameState, handIndex: number): number[] {
  const handTile = state.hand[handIndex];
  if (handTile === undefined) {
    return [];
  }

  return getLegalMoveIndexesForHand(state, handTile);
}

export function getAnyPlayableBoardIndexes(state: GameState): number[] {
  return getAnyLegalMoveIndexes(state);
}

export function applyMove(
  state: GameState,
  handIndex: number,
  boardIndex: number,
  playedAt: number,
  generator: TileGenerator
): GameState {
  if (state.gameOver) {
    return state;
  }

  const handTile = state.hand[handIndex];
  const selectedTile = state.board[boardIndex];
  if (handTile === undefined || selectedTile === undefined) {
    return state;
  }

  const resolution = resolveMove(handTile, selectedTile.value);
  if (resolution.outcome === "illegal") {
    return state;
  }

  const nextTurnIndex = state.turnIndex + 1;
  const recentHighBuildHints = updateRecentHighBuildHints(state, resolution);
  const flow = registerFlowMove(state.flow, playedAt);
  let boardAfterResolution: GameState["board"];

  if (resolution.clearsTile) {
    boardAfterResolution = collapseBoardLeft(state.board, boardIndex);
  } else {
    if (resolution.builtValue === null) {
      return state;
    }
    const builtValue = resolution.builtValue;

    boardAfterResolution = state.board.map((tile, index) =>
      index === boardIndex
        ? {
            id: tile.id,
            value: builtValue,
            source: "built",
            createdTurn: nextTurnIndex,
            buildDepth: tile.buildDepth + 1
          }
        : tile
    );
  }

  const stateAfterMove = drawReplacementHandTile(
    {
      ...state,
      board: boardAfterResolution,
      selectedHandIndex: null,
      turnIndex: nextTurnIndex,
      recentHighBuildHints,
      flow,
      refill: syncRefillState({
        refill: state.refill,
        board: boardAfterResolution,
        flow,
        now: playedAt,
        progression: {
          roundNumber: state.roundNumber,
          score: state.score
        },
        activatedByClear: resolution.clearsTile
      }),
      increment: syncIncrementState({
        increment: state.increment,
        board: boardAfterResolution,
        now: playedAt,
        progression: {
          roundNumber: state.roundNumber,
          score: state.score
        },
        randomSource: generator.nextRandom
      }),
      gameOver: false
    },
    handIndex,
    generator
  );

  if (isRoundCleared(stateAfterMove)) {
    return startNextRound(stateAfterMove, generator, playedAt);
  }

  return finalizeRunState(stateAfterMove);
}

export function applyRefill(state: GameState, expiredAt: number, generator: TileGenerator): GameState {
  if (
    state.gameOver ||
    state.refill.expiresAt !== expiredAt ||
    !state.refill.active ||
    !hasRefillSpace(state.board)
  ) {
    return state;
  }

  const nextTileValue = generator.nextTile({
    reason: "board",
    roundNumber: state.roundNumber,
    state: toGeneratorState(state)
  });
  const board: GameState["board"] = [
    ...state.board,
    {
      id: `b-${state.nextBoardTileId}`,
      value: nextTileValue,
      source: "dealt",
      createdTurn: state.turnIndex,
      buildDepth: 0
    }
  ];

  return finalizeRunState({
    ...state,
    board,
    nextBoardTileId: state.nextBoardTileId + 1,
    refill: syncRefillState({
      refill: {
        ...state.refill,
        expiresAt: null,
        remainingMs: REFILL_WINDOW_MS
      },
      board,
      flow: state.flow,
      now: expiredAt,
      progression: {
        roundNumber: state.roundNumber,
        score: state.score
      }
    }),
    increment: syncIncrementState({
      increment: state.increment,
      board,
      now: expiredAt,
      progression: {
        roundNumber: state.roundNumber,
        score: state.score
      },
      randomSource: generator.nextRandom
    })
  });
}

export function applyIncrement(
  state: GameState,
  laneIndex: number,
  expiredAt: number,
  generator: TileGenerator
): GameState {
  const lane = state.increment.lanes[laneIndex];
  if (state.gameOver || lane === undefined || lane.expiresAt !== expiredAt || lane.tileId === null) {
    return state;
  }

  const boardIndex = state.board.findIndex((tile) => tile.id === lane.tileId);
  if (boardIndex < 0) {
    return finalizeRunState({
      ...state,
      increment: syncIncrementState({
        increment: state.increment,
        board: state.board,
        now: expiredAt,
        progression: {
          roundNumber: state.roundNumber,
          score: state.score
        },
        randomSource: generator.nextRandom
      })
    });
  }

  const targetTile = state.board[boardIndex];
  const board = state.board.map((tile, index) =>
    index === boardIndex
      ? {
          ...tile,
          value: Math.min(9, tile.value + 1)
        }
      : tile
  );
  const increment = syncIncrementState({
    increment: {
      lanes: state.increment.lanes.map((existingLane, existingLaneIndex) =>
        existingLaneIndex === laneIndex
          ? {
              ...existingLane,
              tileId: targetTile.id,
              expiresAt: expiredAt + existingLane.durationMs,
              remainingMs: existingLane.durationMs,
              burstCount: existingLane.burstCount + 1
            }
          : existingLane
      )
    },
    board,
    now: expiredAt,
    progression: {
      roundNumber: state.roundNumber,
      score: state.score
    },
    randomSource: generator.nextRandom
  });

  return finalizeRunState({
    ...state,
    board,
    increment
  });
}
